/**
 * The board as a **card list**, the phone default (P-042 §3, spec M0-F9 §5.2 / F9-D29).
 *
 * ## Why this exists and why it is a deviation with no reference
 * monday.com serves an app interstitial to every phone user agent, login included
 * (`discovery/ui-reference/mobile/INDEX.md`), so there is no mobile web to capture and nothing
 * to diff against — the whole of M0-F9 is invention under the brief's rule *"the best responsive
 * reading of the desktop layout"*. Round 6a's reading was to keep the grid and clamp the name
 * column. P-042 is the user's measurement of what that costs on a **real** board: the sticky name
 * region eats 248 of 390 px and the one data column that fits is whichever the view happens to
 * order first — on the reported board an empty `Creation Log`. A 51-column board read through a
 * 142 px window is not a reading, it is a keyhole.
 *
 * The card list is the same rows arranged the way every list on a phone is arranged: one card per
 * item, the name on top, the status as its chip, the next few columns as label/value rows, the
 * group as a collapsible header with its count. The grid is still one tap away
 * (`LayoutToggle`) for anyone who wants it, and the choice is remembered.
 *
 * ## What it deliberately does not do
 * - **No virtualisation.** The grid virtualises because it paints 51 columns x 454 rows; a card
 *   list paints one card per *loaded* row and the source already pages at 50 per group. Every
 *   loaded row therefore has a card — which is also what the verifier asserts ("cards rendered =
 *   visible items"), and a windowed list could not satisfy that. Off-screen work is skipped by
 *   `content-visibility: auto` in `mobile.css` instead, which costs no DOM and no state.
 * - **No cell editing in place.** Tapping a card opens the item card full-screen (F9-D7), which
 *   is where every column already has its editor on a phone; the grid keeps the in-cell editors.
 *   Two editing surfaces for one value is how they drift apart.
 *
 * It does carry `+ Add <item>` per group and one `Add column`, because those two live in the grid's
 * rows and column-header strip and a list that replaces the grid would otherwise take them away —
 * which `verify-chrome-local.mjs` caught on both phones the moment the card list became the
 * default. They are the same two store actions the grid calls, not a second implementation.
 */
import type { GroupId, ItemId } from '@elizmonday/shared';
import { getColumnType } from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getColumnUi } from '../../columns/registry';
import type { CellRenderContext } from '../../columns/types';
import type { BoardColumn, BoardGroupView, ItemRow } from '../../data/types';
import { useTable, useTableActions } from '../context';
import { toggleItem } from '../selection';
import { visibleColumns } from '../settings';
import { NAME_COLUMN_ID } from '../store';

/**
 * **P-062**: how long a press on a card has to last to tick it.
 *
 * A phone card used to be one `role="button"` and nothing else — no `···`, no checkbox — so
 * below `breakpoints.phone` an item could not be renamed, duplicated, moved, archived or
 * deleted, and no row could be selected, which meant the multi-select bar could never appear.
 * Both affordances below are the same store actions the grid's row calls; monday's own phone
 * card menu was never captured (the account serves an app interstitial to every phone user
 * agent), so the *shape* — a corner checkbox plus a trailing `···`, both 44 px — is **ASSUMED**
 * and filed as **q493**. What is not assumed is that the actions have to be reachable.
 */
const LONG_PRESS_MS = 500;

/** How many label/value rows a card carries. The card asks for "3–4"; four reads without scroll. */
export const PHONE_CARD_COLUMNS = 4;

export interface CardListProps {
  onOpenItem(itemId: ItemId): void;
}

/**
 * Best-effort emptiness, so a card does not print four labels with nothing beside them. The
 * shared definition owns `toText` for every type; a type that throws on a value this board
 * happens to hold must cost the dash, never the list, so the renderer is the fallback.
 */
function isEmptyValue(column: BoardColumn, value: unknown): boolean {
  if (value === null || value === undefined) return true;
  try {
    const text = getColumnType(column.type).toText(value as never, column.settings as never);
    return text === null || text === '';
  } catch {
    return false;
  }
}

function CardValue({
  column,
  item,
  context,
}: {
  column: BoardColumn;
  item: ItemRow;
  context: CellRenderContext;
}) {
  const value = item.values[column.id] ?? null;
  if (isEmptyValue(column, value)) {
    return <span className="ez-pcard__empty">—</span>;
  }
  const ui = getColumnUi(column.type);
  return <ui.Cell value={value} settings={column.settings} column={column} item={item} context={context} />;
}

export function CardList({ onOpenItem }: CardListProps) {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const extraRows = useTable((state) => state.extraRows);
  const cursors = useTable((state) => state.cursors);
  const actions = useTableActions();

  const context: CellRenderContext = useMemo(
    () => ({
      people: payload?.people ?? [],
      columns: payload?.columns ?? [],
      search: effective.search,
      timezone: 'UTC',
    }),
    [payload?.people, payload?.columns, effective.search],
  );

  /**
   * The same `visibleColumns` split the grid uses, so *Hide*, the column order and pinning all
   * mean here exactly what they mean in the table — the card list is a second rendering of one
   * view, not a second view.
   */
  const { status, fields } = useMemo(() => {
    const columns = payload?.columns ?? [];
    const split = visibleColumns(columns, effective, NAME_COLUMN_ID);
    const ordered = [...split.pinned, ...split.scrolling].filter(
      (column) => column.id !== NAME_COLUMN_ID && getColumnUi(column.type).gridColumn !== false,
    );
    const statusColumn = ordered.find((column) => column.type === 'status') ?? null;
    return {
      status: statusColumn,
      fields: ordered.filter((column) => column.id !== statusColumn?.id).slice(0, PHONE_CARD_COLUMNS),
    };
  }, [payload?.columns, effective]);

  const itemSelection = useTable((state) => state.itemSelection);
  const selectedIds = useMemo(() => new Set(itemSelection.ids.map(String)), [itemSelection.ids]);

  if (!payload) return null;

  const terminology = payload.board.itemTerminology;

  const rowsOf = (group: BoardGroupView): readonly ItemRow[] => [
    ...group.page.items,
    ...(extraRows[group.id] ?? []),
  ];

  return (
    <div className="ez-pcards" data-testid="board-cards">
      {payload.groups.map((group) => {
        const rows = rowsOf(group);
        const remaining = cursors[group.id] ? group.page.total - rows.length : 0;
        return (
          <section
            className="ez-pcards__group"
            key={group.id}
            data-testid={`board-cards-group-${group.id}`}
            style={{ ['--ez-pcard-group-color' as string]: group.color }}
          >
            <button
              type="button"
              className="ez-pcards__head"
              aria-expanded={!group.collapsed}
              data-testid={`board-cards-group-toggle-${group.id}`}
              onClick={() => void actions.toggleGroupCollapsed(group.id as GroupId, !group.collapsed)}
            >
              <Icon
                icon={group.collapsed ? icons.chevronRight : icons.chevronDown}
                size={16}
                ignoreFocusStyle
              />
              <span className="ez-pcards__title">{group.title}</span>
              <span className="ez-pcards__count">{`${String(group.page.total)} ${
                group.page.total === 1 ? 'item' : 'items'
              }`}</span>
            </button>

            {group.collapsed ? null : (
              <ul className="ez-pcards__list">
                {rows.map((item) => (
                  <li key={item.id}>
                    <PhoneCard
                      item={item}
                      status={status}
                      fields={fields}
                      context={context}
                      selected={selectedIds.has(String(item.id))}
                      onOpen={() => onOpenItem(item.id)}
                    />
                  </li>
                ))}

                {remaining > 0 ? (
                  <li>
                    <button
                      type="button"
                      className="ez-pcards__more"
                      data-testid={`board-cards-more-${group.id}`}
                      onClick={() => void actions.loadMore(group.id as GroupId)}
                    >
                      {`Load ${String(remaining)} more`}
                    </button>
                  </li>
                ) : null}

                {rows.length === 0 ? <li className="ez-pcards__empty">No items</li> : null}

                <li>
                  <AddCard groupId={group.id as GroupId} terminology={terminology} />
                </li>
              </ul>
            )}
          </section>
        );
      })}

      {/*
        * The grid's column-header strip owns `add-column`; without it a phone in card mode could
        * read four of a board's fifty-one columns and never add a fifty-second.
        */}
      {payload.permissions.canEditStructure ? (
        <button
          type="button"
          className="ez-pcards__add-column"
          data-testid="add-column"
          aria-label="Add column"
          onClick={() => actions.openPane('add-column')}
        >
          <Icon icon={icons.add} size={16} ignoreFocusStyle />
          Add column
        </button>
      ) : null}
    </div>
  );
}

/**
 * One card (P-062). It carries monday's own row affordances, sized for a finger:
 *
 *   - a **checkbox** in the card's leading corner, which ticks the row through the very same
 *     `itemSelection` the grid uses, so the multi-select bar it raises is the same bar with the
 *     same six actions;
 *   - a trailing **`···`** that opens the same `ItemMenu` the grid's row opens — `TableView`
 *     renders the menu for `menu.kind === 'item'` whichever layout is mounted, so there is one
 *     row-menu implementation and not two;
 *   - a **long press** anywhere on the card, the phone idiom for entering selection, which
 *     ticks the row and suppresses the tap that would otherwise open the item card.
 */
function PhoneCard({
  item,
  status,
  fields,
  context,
  selected,
  onOpen,
}: {
  item: ItemRow;
  status: BoardColumn | null;
  fields: readonly BoardColumn[];
  context: CellRenderContext;
  selected: boolean;
  onOpen(): void;
}) {
  const actions = useTableActions();
  const itemSelection = useTable((state) => state.itemSelection);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set by a long press, read by the `click` that follows it, cleared straight afterwards. */
  const pressed = useRef(false);

  const cancel = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => cancel, []);

  const toggleSelected = () => actions.setItemSelection(toggleItem(itemSelection, item.id));

  return (
    <div
      className="ez-pcard"
      role="button"
      tabIndex={0}
      aria-selected={selected}
      data-testid={`board-card-${item.id}`}
      data-item={item.id}
      data-selected={selected || undefined}
      onPointerDown={() => {
        pressed.current = false;
        cancel();
        timer.current = setTimeout(() => {
          pressed.current = true;
          toggleSelected();
        }, LONG_PRESS_MS);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onClick={() => {
        cancel();
        // The press that ticked the row must not also open the item card.
        if (pressed.current) {
          pressed.current = false;
          return;
        }
        onOpen();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="ez-pcard__top">
        <input
          type="checkbox"
          className="ez-pcard__select"
          checked={selected}
          aria-label={`Select ${item.name}`}
          onChange={() => undefined}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            toggleSelected();
          }}
        />
        <span className="ez-pcard__name">{item.name}</span>
        {/* An empty status is no chip at all — an em dash in the corner of a card
            reads as a value the card could not render. */}
        {status && !isEmptyValue(status, item.values[status.id] ?? null) ? (
          <span className="ez-pcard__chip" data-column={status.id}>
            <CardValue column={status} item={item} context={context} />
          </span>
        ) : null}
        <button
          type="button"
          className="ez-pcard__menu"
          aria-label={`More options for ${item.name}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            const box = event.currentTarget.getBoundingClientRect();
            actions.openMenu({ kind: 'item', targetId: String(item.id), x: box.right, y: box.bottom });
          }}
        >
          <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
        </button>
      </span>

      {fields.length > 0 ? (
        <span className="ez-pcard__fields">
          {fields.map((column) => (
            <span className="ez-pcard__field" key={column.id} data-column={column.id}>
              <span className="ez-pcard__label">{column.title}</span>
              <span className="ez-pcard__value">
                <CardValue column={column} item={item} context={context} />
              </span>
            </span>
          ))}
        </span>
      ) : null}

      {item.updatesCount > 0 || item.subitemIds.length > 0 ? (
        <span className="ez-pcard__meta">
          {item.updatesCount > 0 ? (
            <span className="ez-pcard__badge">
              <Icon icon={icons.comment} size={14} ignoreFocusStyle />
              {item.updatesCount}
            </span>
          ) : null}
          {item.subitemIds.length > 0 ? (
            <span className="ez-pcard__badge">
              <Icon icon={icons.subitem} size={14} ignoreFocusStyle />
              {item.subitemIds.length}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/**
 * `+ Add <item>` for one group — the card-list reading of `AddItemRow`, calling the same
 * `actions.addItem`. Enter commits and keeps the field open, as the grid's row does; Escape and
 * an empty name create nothing.
 */
function AddCard({ groupId, terminology }: { groupId: GroupId; terminology: string }) {
  const actions = useTableActions();
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState('');

  const commit = async (keepGoing: boolean) => {
    const name = draft.trim();
    setDraft('');
    if (name === '') {
      setDrafting(false);
      return;
    }
    await actions.addItem(groupId, name);
    setDrafting(keepGoing);
  };

  if (!drafting) {
    return (
      <button
        type="button"
        className="ez-pcards__add"
        data-testid={`add-item-${groupId}`}
        onClick={() => setDrafting(true)}
      >
        {`+ Add ${terminology}`}
      </button>
    );
  }

  return (
    <input
      className="ez-pcards__draft"
      aria-label={`New ${terminology}`}
      data-testid={`add-item-input-${groupId}`}
      value={draft}
      autoFocus
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') void commit(true);
        if (event.key === 'Escape') {
          setDraft('');
          setDrafting(false);
        }
      }}
      onBlur={() => void commit(false)}
    />
  );
}
